import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Badge,
  IconButton,
  Tooltip,
  Input,
  Stack,
  Textarea,
  HStack,
  VStack,
  Divider,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, CheckIcon, ViewIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card.js';
import { getData, postData, putData, patchData, deleteData } from '../../../utils/httpClient';
import { useSearch } from '../../../contexts/SearchContext';

export default function LibraryLoans() {
  const [loans, setLoans] = useState([]);
  const [mediaList, setMediaList] = useState([]);
  const [personsList, setPersonsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMedia, setLoadingMedia] = useState(true);
  const [loadingPersons, setLoadingPersons] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedLoan, setSelectedLoan] = useState(null);
  
  const { searchQuery } = useSearch();

  const [formData, setFormData] = useState({
    mediaId: '',
    personId: '',
    loanDate: '',
    dueDate: '',
    returnDate: '',
    status: 'ACTIVE',
    notes: '',
  });

  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const formatPersonFullName = (person) => {
    if (!person) return '';
    const firstName = person.name || '';
    const secondName = person.second_name || '';
    const lastName = person.last_name || '';
    const maternalLastName = person.maternal_last_name || '';
    
    const fullName = `${firstName}${secondName ? ' ' + secondName : ''} ${lastName}${maternalLastName ? ' ' + maternalLastName : ''}`.trim();
    return fullName;
  };

  const formatPersonNames = (person) => {
    if (!person) return '';
    const firstName = person.name || '';
    const secondName = person.secondName || '';
    return `${firstName}${secondName ? ' ' + secondName : ''}`.trim();
  };

  const formatPersonLastNames = (person) => {
    console.log(person)
    if (!person) return '';
    const lastName = person.lastName || '';
    const maternalLastName = person.maternalLastName || '';
    return `${lastName}${maternalLastName ? ' ' + maternalLastName : ''}`.trim();
  };

  const fetchLoans = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching loans...');
      const response = await getData('/library/loans');
      console.log('Library loans response:', response);
      
      if (Array.isArray(response)) {
        setLoans(response);
      } else if (response && Array.isArray(response.data)) {
        setLoans(response.data);
      } else {
        console.warn('Response is not an array:', response);
        setLoans([]);
      }
    } catch (error) {
      console.error('Error fetching loans:', error);
      setLoans([]);
      toast({
        title: 'Error',
        description: 'Error al cargar los préstamos',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchMedia = useCallback(async () => {
    try {
      setLoadingMedia(true);
      const response = await getData('/library/media');
      
      if (Array.isArray(response)) {
        setMediaList(response);
      } else if (response && Array.isArray(response.data)) {
        setMediaList(response.data);
      } else {
        setMediaList([]);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
      setMediaList([]);
    } finally {
      setLoadingMedia(false);
    }
  }, []);

  const fetchPersons = useCallback(async () => {
    try {
      setLoadingPersons(true);
      const response = await getData('/library/loans/persons');
      
      if (Array.isArray(response)) {
        setPersonsList(response);
      } else if (response && Array.isArray(response.data)) {
        setPersonsList(response.data);
      } else {
        setPersonsList([]);
      }
    } catch (error) {
      console.error('Error fetching persons:', error);
      setPersonsList([]);
    } finally {
      setLoadingPersons(false);
    }
  }, []);

  useEffect(() => {
    fetchLoans();
    fetchMedia();
    fetchPersons();
  }, [fetchLoans, fetchMedia, fetchPersons]);

  const handleCreateLoan = () => {
    setModalMode('create');
    setSelectedLoan(null);
    setFormData({
      mediaId: '',
      personId: '',
      loanDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 días
      returnDate: '',
      status: 'ACTIVE',
      notes: '',
    });
    setIsModalOpen(true);
  };

  const handleEditLoan = (loan) => {
    setModalMode('edit');
    setSelectedLoan(loan);
    setFormData({
      mediaId: loan.mediaId?.toString() || '',
      personId: loan.personId?.toString() || '',
      loanDate: loan.loanDate || '',
      dueDate: loan.dueDate || '',
      returnDate: loan.returnDate || '',
      status: loan.status || 'ACTIVE',
      notes: loan.notes || '',
    });
    setIsModalOpen(true);
  };

  const handleViewLoan = (loan) => {
    setModalMode('view');
    setSelectedLoan(loan);
    setFormData({
      mediaId: loan.mediaId?.toString() || '',
      personId: loan.personId?.toString() || '',
      loanDate: loan.loanDate || '',
      dueDate: loan.dueDate || '',
      returnDate: loan.returnDate || '',
      status: loan.status || 'ACTIVE',
      notes: loan.notes || '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (modalMode === 'create') {
        const response = await postData('/library/loans', formData);
        if (response.success) {
          toast({
            title: 'Éxito',
            description: 'Préstamo creado exitosamente',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          fetchLoans();
          setIsModalOpen(false);
        } else {
          throw new Error(response.message);
        }
      } else if (modalMode === 'edit') {
        const response = await patchData(`/library/loans/${selectedLoan.id}`, formData);
        if (response.success) {
          toast({
            title: 'Éxito',
            description: 'Préstamo actualizado exitosamente',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          fetchLoans();
          setIsModalOpen(false);
        } else {
          throw new Error(response.message);
        }
      }
    } catch (error) {
      console.error('Error submitting loan:', error);
      toast({
        title: 'Error',
        description: error.message || 'Error al procesar el préstamo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleReturn = async (loan) => {
    try {
      const response = await putData(`/library/loans/${loan.id}/return`, {
        returnDate: new Date().toISOString().split('T')[0]
      });
      
      if (response.success) {
        toast({
          title: 'Éxito',
          description: 'Devolución registrada exitosamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchLoans();
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Error returning media:', error);
      toast({
        title: 'Error',
        description: error.message || 'Error al registrar la devolución',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (loan) => {
    if (window.confirm('¿Está seguro de que desea eliminar este préstamo?')) {
      try {
        const response = await deleteData(`/library/loans/${loan.id}`);
        if (response.success) {
          toast({
            title: 'Éxito',
            description: 'Préstamo eliminado exitosamente',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          fetchLoans();
        } else {
          throw new Error(response.message);
        }
      } catch (error) {
        console.error('Error deleting loan:', error);
        toast({
          title: 'Error',
          description: error.message || 'Error al eliminar el préstamo',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      ACTIVE: 'blue',
      RETURNED: 'green',
      OVERDUE: 'red'
    };
    
    const labels = {
      ACTIVE: 'Activo',
      RETURNED: 'Devuelto',
      OVERDUE: 'Vencido'
    };

    return (
      <Badge colorScheme={colors[status] || 'gray'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const filteredLoans = loans.filter(loan => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      (loan.person?.last_name || '').toLowerCase().includes(searchLower) ||
      (loan.person?.maternal_last_name || '').toLowerCase().includes(searchLower) ||
      (loan.person?.name || '').toLowerCase().includes(searchLower) ||
      (loan.person?.second_name || '').toLowerCase().includes(searchLower) ||
      (loan.person?.document || '').toLowerCase().includes(searchLower) ||
      (loan.media?.title || '').toLowerCase().includes(searchLower) ||
      (loan.media?.code || '').toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Text>Cargando préstamos...</Text>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card
        direction="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Flex px="25px" justify="space-between" align="center">
          <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
            Préstamos de Biblioteca
          </Text>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            variant="solid"
            onClick={handleCreateLoan}
          >
            Nuevo Préstamo
          </Button>
        </Flex>

        <Box overflowX="auto">
          <Table variant="simple" color="gray.500" mb="24px">
            <Thead>
              <Tr>
                <Th pe="10px" borderColor={borderColor}>
                  ID
                </Th>
                <Th pe="10px" borderColor={borderColor}>
                  Medio
                </Th>
                <Th pe="10px" borderColor={borderColor}>
                  Apellidos del Solicitante
                </Th>
                <Th pe="10px" borderColor={borderColor}>
                  Nombres del Solicitante
                </Th>
                <Th pe="10px" borderColor={borderColor}>
                  Documento
                </Th>
                <Th pe="10px" borderColor={borderColor}>
                  Fecha Préstamo
                </Th>
                <Th pe="10px" borderColor={borderColor}>
                  Fecha Vencimiento
                </Th>
                <Th pe="10px" borderColor={borderColor}>
                  Fecha Devolución
                </Th>
                <Th pe="10px" borderColor={borderColor}>
                  Estado
                </Th>
                <Th pe="10px" borderColor={borderColor}>
                  Acciones
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredLoans.map((loan) => (
                <Tr key={loan.id} _hover={{ bg: hoverBg }}>
                  <Td color={textColor} fontSize="sm" fontWeight="700">
                    {loan.id}
                  </Td>
                  <Td color={textColor} fontSize="sm">
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="600">{loan.media?.title || 'Medio no encontrado'}</Text>
                      <Text fontSize="xs" color="gray.500">{loan.media?.code}</Text>
                    </VStack>
                  </Td>
                  <Td color={textColor} fontSize="sm">
                    {formatPersonLastNames(loan.person) || '-'}
                  </Td>
                  <Td color={textColor} fontSize="sm">
                    {formatPersonNames(loan.person) || '-'}
                  </Td>
                  <Td color={textColor} fontSize="sm">
                    {loan.person?.document || '-'}
                  </Td>
                  <Td color={textColor} fontSize="sm">
                    {formatDate(loan.loanDate)}
                  </Td>
                  <Td color={textColor} fontSize="sm">
                    {formatDate(loan.dueDate)}
                  </Td>
                  <Td color={textColor} fontSize="sm">
                    {formatDate(loan.returnDate)}
                  </Td>
                  <Td>
                    {getStatusBadge(loan.status)}
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Tooltip label="Ver detalles">
                        <IconButton
                          icon={<ViewIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleViewLoan(loan)}
                        />
                      </Tooltip>
                      {loan.status !== 'RETURNED' && (
                        <Tooltip label="Editar">
                          <IconButton
                            icon={<EditIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="orange"
                            onClick={() => handleEditLoan(loan)}
                          />
                        </Tooltip>
                      )}
                      {loan.status === 'ACTIVE' && (
                        <Tooltip label="Marcar como devuelto">
                          <IconButton
                            icon={<CheckIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="green"
                            onClick={() => handleReturn(loan)}
                          />
                        </Tooltip>
                      )}
                      <Tooltip label="Eliminar">
                        <IconButton
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDelete(loan)}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        {filteredLoans.length === 0 && (
          <Flex justify="center" align="center" py="40px">
            <Text color="gray.500">No se encontraron préstamos</Text>
          </Flex>
        )}
      </Card>

      {/* Modal para crear/editar/ver préstamo */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>
            {modalMode === 'create' && 'Nuevo Préstamo'}
            {modalMode === 'edit' && 'Editar Préstamo'}
            {modalMode === 'view' && 'Detalles del Préstamo'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <HStack spacing={4} align="start">
                <VStack spacing={4} flex={1}>
                  <FormControl isRequired={modalMode !== 'view'}>
                    <FormLabel>Medio</FormLabel>
                    <Select
                      value={formData.mediaId}
                      onChange={(e) => setFormData({...formData, mediaId: e.target.value})}
                      isDisabled={modalMode === 'view'}
                      placeholder="Seleccione un medio"
                    >
                      {mediaList.map((media) => (
                        <option key={media.id} value={media.id}>
                          {media.title} ({media.code})
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl isRequired={modalMode !== 'view'}>
                    <FormLabel>Solicitante</FormLabel>
                    <Select
                      value={formData.personId}
                      onChange={(e) => setFormData({...formData, personId: e.target.value})}
                      isDisabled={modalMode === 'view'}
                      placeholder="Seleccione un solicitante"
                    >
                      {personsList.map((person) => (
                        <option key={person.id} value={person.id}>
                          {`${formatPersonNames(person)} ${formatPersonLastNames(person)} - ${person.document || ''}`}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </VStack>

                <VStack spacing={4} flex={1}>
                  <FormControl isRequired={modalMode !== 'view'}>
                    <FormLabel>Fecha de Préstamo</FormLabel>
                    <Input
                      type="date"
                      value={formData.loanDate}
                      onChange={(e) => setFormData({...formData, loanDate: e.target.value})}
                      isDisabled={modalMode === 'view'}
                    />
                  </FormControl>

                  <FormControl isRequired={modalMode !== 'view'}>
                    <FormLabel>Fecha de Vencimiento</FormLabel>
                    <Input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                      isDisabled={modalMode === 'view'}
                    />
                  </FormControl>

                  {(modalMode === 'edit' || modalMode === 'view') && (
                    <FormControl>
                      <FormLabel>Fecha de Devolución</FormLabel>
                      <Input
                        type="date"
                        value={formData.returnDate}
                        onChange={(e) => setFormData({...formData, returnDate: e.target.value})}
                        isDisabled={modalMode === 'view'}
                      />
                    </FormControl>
                  )}

                  {(modalMode === 'edit' || modalMode === 'view') && (
                    <FormControl>
                      <FormLabel>Estado</FormLabel>
                      <Select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        isDisabled={modalMode === 'view'}
                      >
                        <option value="ACTIVE">Activo</option>
                        <option value="RETURNED">Devuelto</option>
                        <option value="OVERDUE">Vencido</option>
                      </Select>
                    </FormControl>
                  )}
                </VStack>
              </HStack>

              <Divider />

              <FormControl>
                <FormLabel>Notas</FormLabel>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  isDisabled={modalMode === 'view'}
                  placeholder="Observaciones adicionales..."
                  rows={3}
                />
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>
              {modalMode === 'view' ? 'Cerrar' : 'Cancelar'}
            </Button>
            {modalMode !== 'view' && (
              <Button colorScheme="blue" onClick={handleSubmit}>
                {modalMode === 'create' ? 'Crear' : 'Actualizar'}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
